import { createEffect } from "solid-js"
import { ConeGeometry, Group, LOD, Mesh, MeshLambertMaterial, PlaneGeometry, type BufferGeometry } from "three"
import { onCleanupRemoveAndDispose } from "../../../../../rx/cleanup/onCleanupRemoveAndDispose"
import { createSharedTileGraphNodeCollectionMemo } from "../../../../../rx/memo/createSharedTileGraphNodeCollectionMemo"
import { createTriggerSignal } from "../../../../../rx/signal/create_trigger_signal"
import type { Engine } from "../../../../core/engine/Engine.type"
import type { TileGraphNode } from "../../../../core/engine/TileGraphNode/TileGraphNode.type"
import { mountOnFrameUpdateToObject3D } from "../../../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import type { QuadrantBound } from "../../../../Quadtree/createEntityQuadtreeEffect"
import createTileGraphNodeQuadtree from "../../../../Quadtree/createTileGraphNodeQuadtree"
import { VERTICES_PER_TILE } from "../../../consts"
import rotatePlaneToLayFlat from "../../../fn/rotatePlaneToLayFlat"
import useMainScene from "../../../fn/useMainScene"
import { useRootQuadrantBound } from "../../../sky/useRootQuadrantBound"
import createTerrainMesh1V3WaterTileSurfaceGroup from "./createTerrainMesh1V3WaterTileSurfaceGroup"
import createTerrainMesh1V3WaterTileSurfaceLowMesh from "./createTerrainMesh1V3WaterTileSurfaceLowMesh"
import createWaterPlaneV7BasicLambertMaterial from "./createWaterPlaneV7BasicLambertMaterial"
import createWaterPlaneV7LambertWithShaderV2Material from "./createWaterPlaneV7LambertWithShaderV2Material"
import createWaterPlaneV7ToonWaterMaterial from "./createWaterPlaneV7ToonWaterMaterial"

const USE_LOD_DEBUG_CONE = false
const USE_LOD_DEBUG_PLANE = false

const USE_LOW_MESH = false

export enum W7MaterialVersion {
  BasicLambert = 1,
  ToonWater = 2,
  LambertWithShader = 3,
  LambertWithShaderV2 = 4,
}
export const W7_MATERIAL_VERSION : W7MaterialVersion = W7MaterialVersion.LambertWithShaderV2

export default function createWaterPlaneMeshV7Effect (
  engine: Engine
) {
  const scene = useMainScene(engine)

  const getTileGraphNodesList = createSharedTileGraphNodeCollectionMemo(engine)
  // const getGridSize = useGridWidthHeight(engine)
  const getRootQuadrantBound = useRootQuadrantBound(engine)
  const maxChunkSize = 4
  createEffect(() => {
    const tpNodeList = getTileGraphNodesList()
    const rootQuadrant = getRootQuadrantBound()
    const outerGroup = new Group()
    createTileGraphNodeQuadtree(tpNodeList, rootQuadrant, maxChunkSize, (
      subNodeList: Array<TileGraphNode>,
      quadrant: QuadrantBound,
    ) => {
      // console.log('createTerrainMesh1V3QuadTreeEffect', quadrant, subNodeList.length)

      const lod = new LOD()
      onCleanupRemoveAndDispose(lod)
      lod.position.set((quadrant.x + 1.5) * VERTICES_PER_TILE, 0, (quadrant.y + 1.5) * VERTICES_PER_TILE)
      outerGroup.add(lod)

      const geometryLowList: Array<BufferGeometry> = []
      const [trackGeometryLowList, dirtyGeometryLowList] = createTriggerSignal()
      const materialList: Array<MeshLambertMaterial> = []
      const [trackMaterialList, dirtyMaterialList] = createTriggerSignal()
      // closest LOD
      {
        const lodDistance = 0
        const quadGroup = createTerrainMesh1V3WaterTileSurfaceGroup(
          engine, subNodeList, quadrant,
          geometryLowList, dirtyGeometryLowList,
          materialList, dirtyMaterialList,
        )
        rotatePlaneToLayFlat(quadGroup)
        quadGroup.position.x -= (1.5 * VERTICES_PER_TILE)
        quadGroup.position.z -= (1.5 * VERTICES_PER_TILE)
        lod.addLevel(quadGroup, lodDistance)
      }

      // middle LOD
      {
        const lodDistance = 100
        // const lodDistance = 40
        if (USE_LOD_DEBUG_PLANE) {
          const geometry = new PlaneGeometry(
            VERTICES_PER_TILE * quadrant.width, VERTICES_PER_TILE * quadrant.height,
            quadrant.width, quadrant.height)
          rotatePlaneToLayFlat(geometry)

          const mesh = new Mesh(geometry)
          mesh.position.y += 1
          lod.addLevel(mesh, lodDistance)

          if (W7_MATERIAL_VERSION === W7MaterialVersion.BasicLambert) {
            const material = createWaterPlaneV7BasicLambertMaterial(engine)
            mesh.material = material
          } else if (W7_MATERIAL_VERSION === W7MaterialVersion.LambertWithShader) {
            const material = createWaterPlaneV7BasicLambertMaterial(engine)
            mesh.material = material
          } else if (W7_MATERIAL_VERSION === W7MaterialVersion.LambertWithShaderV2) {
            const material = createWaterPlaneV7LambertWithShaderV2Material(engine)
            mesh.material = material
            // const { uniforms } = material
            // mountOnFrameUpdateToObject3D(engine, mesh, (dt: number, at: number): void => {
            //   uniforms.u_time.value = at / 1000
            // })
          } else {
            const material = createWaterPlaneV7ToonWaterMaterial(engine)
            mesh.material = material
            const { uniforms } = material
            mountOnFrameUpdateToObject3D(engine, mesh, (dt: number, at: number): void => {
              uniforms.u_time.value = at / 1000
            })
          }
        } else if (USE_LOW_MESH) {
          const quadMesh = createTerrainMesh1V3WaterTileSurfaceLowMesh(
            engine,
            quadrant,
            geometryLowList,
            trackGeometryLowList,
            materialList,
            trackMaterialList,
          )
          rotatePlaneToLayFlat(quadMesh)
          quadMesh.position.x -= (1.5 * VERTICES_PER_TILE)
          quadMesh.position.z -= (1.5 * VERTICES_PER_TILE)
          lod.addLevel(quadMesh, lodDistance)
          onCleanupRemoveAndDispose(quadMesh)
          // createMountedInterval(() => {
          //   // console.log('lod.levels', lod.levels)
          //   if (lod.levels[1].distance === 1) {
          //     lod.levels[1].distance = 100
          //   } else {
          //     lod.levels[1].distance = 1
          //   }
          // }, 2000)
        }
      }

      // furthest away LOD
      // {
      //   // lod.addLevel(quadGroup, 250)
      // }

      if (USE_LOD_DEBUG_CONE) {
        const mesh = new Mesh(new ConeGeometry(), new MeshLambertMaterial({ color: 0xffffff }))
        outerGroup.add(mesh)
        mesh.position.copy(lod.position)
        mesh.position.y += 2
        mesh.scale.setScalar(5)
      }
    })
    scene.add(outerGroup)
    // group.position.x += VERTICES_PER_TILE * 1.5
    // group.position.z += VERTICES_PER_TILE * 1.5
    // outerGroup.position.y += VERTICES_PER_TILE * 0.1
    onCleanupRemoveAndDispose(outerGroup)
  })


}